import * as React from 'react';
import clsx from 'clsx';
import isHotkey from 'is-hotkey';
import type { ReactNode } from 'react';

import ChevronDownIcon from '@app/components/icons/chevron-down';
import ChevronUpIcon from '@app/components/icons/chevron-up';
import Button from '@app/components/button';
import { setItemInContractList, getItemInContractList } from '@flow/libs/utils/local-storage';

import style from './filter-box.module.scss';

type Props = {
  title: ReactNode;
  children: ReactNode;
  count?: number;
  workspaceId?: Oneflow.Workspace['id'];
  param?: string;
  removeDivider?: boolean;
  headerButton?: ReactNode;
};

type State = {
  visible: boolean;
};

export default class FilterBox extends React.Component<Props, State> {
  getInitialState = (): boolean => {
    const { param, workspaceId } = this.props;

    return getItemInContractList({
      visible: true,
      workspaceId,
      param: param || '',
    });
  };

  state = {
    visible: this.getInitialState(),
  };

  componentDidUpdate(prevProps: Props) {
    const { workspaceId } = this.props;
    const { workspaceId: prevWorkspaceId } = prevProps;

    if (typeof workspaceId !== 'number' || typeof prevWorkspaceId !== 'number') {
      return;
    }

    if (workspaceId !== prevWorkspaceId) {
      this.updateState();
    }
  }

  updateState = () => {
    this.setState({
      visible: this.getInitialState(),
    });
  };

  toggleVisibility = () => {
    const { workspaceId, param } = this.props;

    this.setState(
      (prevState: State) => ({
        visible: !prevState.visible,
      }),
      () => {
        setItemInContractList({
          visible: this.state.visible,
          workspaceId,
          param: param || '',
        });
      },
    );
  };

  getIcon = (): ReactNode => {
    const { visible } = this.state;

    if (visible) {
      return <ChevronUpIcon height="10px" />;
    }

    return <ChevronDownIcon height="10px" />;
  };

  renderFilter() {
    const { children } = this.props;
    const { visible } = this.state;

    if (!visible) {
      return null;
    }

    return <div className={style.Content}>{children}</div>;
  }

  renderFilterCounter = (count?: number) => {
    if (!count) {
      return null;
    }

    return <div className={style.FilterCounter}>{count}</div>;
  };

  render() {
    const { count, removeDivider, title, headerButton } = this.props;

    const filterBoxClassNames = clsx(style.FilterBox, {
      [style.RemoveDivider]: removeDivider,
      [style.HasHeaderButton]: Boolean(headerButton),
    });

    if (headerButton) {
      return (
        <div
          className={filterBoxClassNames}
          data-testid="filter"
        >
          <div className={style.Header}>
            <div
              className={style.HeaderContent}
              onClick={() => {
                this.toggleVisibility();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (isHotkey('enter', e) || isHotkey('space', e)) {
                  e.stopPropagation();
                  this.toggleVisibility();
                }
              }}
            >
              <h3 className={style.Title}>{title}</h3>
              {this.renderFilterCounter(count)}
            </div>
            <div className={style.HeaderActions}>
              {headerButton}
              <Button
                icon={this.getIcon()}
                onClick={(e) => {
                  e.stopPropagation();
                  this.toggleVisibility();
                }}
                hasIconRight
              />
            </div>
          </div>
          {this.renderFilter()}
        </div>
      );
    }

    return (
      <div
        className={filterBoxClassNames}
        data-testid="filter"
      >
        <Button
          icon={this.getIcon()}
          customClass={style.Header}
          onClick={this.toggleVisibility}
          hasIconRight
        >
          <div className={style.HeaderContent}>
            <h3 className={style.Title}>{title}</h3>
            {this.renderFilterCounter(count)}
          </div>
        </Button>
        {this.renderFilter()}
      </div>
    );
  }
}
